import { useEffect, useId, useRef, useState } from 'react';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useSupabase } from '@/hooks/useSupabase';
import {
  configureGmailSignature,
  signaturePreviewDocument,
  type GmailSignatureApplied,
  type GmailSignaturePreview
} from '@/services/gmailSignatureService';

interface ConfigureGmailSignatureProps {
  targetUserId: string;
  isOwnProfile?: boolean;
  hasUnsavedChanges: boolean;
  disabledReason?: string;
}

export function ConfigureGmailSignature({ targetUserId, isOwnProfile = false, hasUnsavedChanges, disabledReason }: ConfigureGmailSignatureProps) {
  const client = useSupabase();
  const descriptionId = useId();
  const setupId = useId();
  const requestId = useRef(0);
  const applying = useRef(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<GmailSignaturePreview | null>(null);
  const [applied, setApplied] = useState<GmailSignatureApplied | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reason = hasUnsavedChanges ? 'Save profile changes before configuring the Gmail signature.' : disabledReason;

  useEffect(() => {
    requestId.current += 1;
    setOpen(false);
    setPreview(null);
    setApplied(null);
    setError(null);
    return () => {
      requestId.current += 1;
    };
  }, [targetUserId]);

  const loadPreview = async () => {
    if (reason || applying.current) {
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    setPreview(null);
    setApplied(null);
    setError(null);
    try {
      const result = await configureGmailSignature(client, { action: 'preview', targetUserId });
      if (id === requestId.current && result.status === 'preview') {
        setPreview(result);
      }
    } catch (failure) {
      if (id === requestId.current) {
        setError(failure instanceof Error ? failure.message : 'Could not load the signature preview.');
      }
    } finally {
      if (id === requestId.current) {
        setLoading(false);
      }
    }
  };

  const apply = async () => {
    if (!preview || preview.targetUserId !== targetUserId || reason || applying.current) {
      return;
    }
    applying.current = true;
    setSaving(true);
    setError(null);
    const id = ++requestId.current;
    try {
      const result = await configureGmailSignature(client, {
        action: 'apply', targetUserId, previewVersion: preview.previewVersion
      });
      if (id === requestId.current && result.status === 'applied') {
        setApplied(result);
        setPreview(null);
      }
    } catch (failure) {
      if (id === requestId.current) {
        setPreview(null);
        setError(failure instanceof Error ? failure.message : 'Could not verify the Gmail signature update.');
      }
    } finally {
      applying.current = false;
      if (id === requestId.current) {
        setSaving(false);
      }
    }
  };

  return (
    <div className="space-y-2 border-t pt-4">
      <Dialog open={open} onOpenChange={nextOpen => {
        if (applying.current) {
          return;
        }
        setOpen(nextOpen);
        if (nextOpen) {
          void loadPreview();
        } else {
          requestId.current += 1;
          setLoading(false);
        }
      }}>
        <DialogTrigger asChild>
          <Button type="button" variant="outline" className="h-auto min-h-10 max-w-full whitespace-normal" disabled={Boolean(reason)} aria-describedby={descriptionId}>
            <Mail className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
            Configure Signature Line
          </Button>
        </DialogTrigger>
        <DialogContent className="lw-ds max-h-[90dvh] max-w-2xl overflow-y-auto" hideCloseButton={saving}>
          <DialogHeader>
            <DialogTitle>Configure Signature Line</DialogTitle>
            <DialogDescription className="text-foreground">
              Review the Lendwise signature generated from {isOwnProfile ? 'your' : 'this user’s'} saved profile, including the animated owl.
            </DialogDescription>
          </DialogHeader>
          {reason && <p role="alert" className="text-sm text-warning">{reason}</p>}
          {loading && <p role="status" className="flex items-center text-sm"><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />Loading Gmail signature preview…</p>}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {applied && (
            <>
              <p role="status" className="break-words text-sm font-medium text-primary">Signature content saved and verified in Gmail for {applied.email}.</p>
              <section aria-labelledby={setupId} className="space-y-2 rounded-md border bg-muted/50 p-3 text-sm text-foreground">
                <h3 id={setupId} className="font-medium">Finish setup in Gmail</h3>
                <p className="break-words">Open Gmail for {applied.email}, then:</p>
                <ol className="list-decimal space-y-2 pl-5">
                  <li><strong>Refresh Gmail.</strong> Reload the browser tab before opening Settings or starting a new message.</li>
                  <li><strong>Find or rename the signature.</strong> Go to Settings → See all settings → General → Signature. Gmail may call it “My Signature.” To rename it, click the pencil beside its name and enter “Lendwise Signature.”</li>
                  <li><strong>Check signature defaults.</strong> Select the updated signature for “For new emails use” and “On reply/forward use,” then click “Save Changes” at the bottom of Gmail Settings.</li>
                  <li><strong>Check a new message and a reply.</strong> Confirm each contains the updated signature.</li>
                </ol>
                <p>Saving here verifies the signature content. Its name and default selections need to be checked in Gmail.</p>
              </section>
            </>
          )}
          {(preview || applied) && (
            <>
              <p className="break-all text-sm font-medium">Mailbox: {preview?.email || applied?.email}</p>
              <iframe
                title="Gmail signature preview"
                sandbox=""
                referrerPolicy="no-referrer"
                srcDoc={signaturePreviewDocument(preview?.html || applied?.html || '')}
                className="h-[460px] w-full min-w-0 rounded-md border bg-white"
              />
              {preview?.warnings.map(warning => <p key={warning} className="text-sm text-warning">{warning}</p>)}
              {preview && <p className="text-sm text-foreground">{preview.replacesExisting ? 'Applying replaces this mailbox’s current signature. A recovery copy is retained.' : 'Applying adds this signature to the mailbox.'} Mobile-app signatures and other send-as addresses are managed separately.</p>}
            </>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={() => {
              requestId.current += 1;
              setOpen(false);
            }}>
              {applied ? 'Done' : 'Cancel'}
            </Button>
            {!applied && <Button type="button" variant="outline" disabled={loading || saving || Boolean(reason)} onClick={() => void loadPreview()}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />Refresh preview
            </Button>}
            {!applied && <Button type="button" disabled={!preview || loading || saving || Boolean(reason)} onClick={() => void apply()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {saving ? 'Applying…' : 'Apply to Gmail'}
            </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <p id={descriptionId} className="text-xs text-foreground">{reason || `Preview and apply ${isOwnProfile ? 'your' : 'this user’s'} branded Gmail signature.`}</p>
    </div>
  );
}
